import React, { useContext, useEffect } from "react";
import { styled } from "@mui/material/styles";
import Accordion from '@mui/material/Accordion';
import { 
  AccordionSummary,
  AccordionDetails,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Tooltip
} from "@mui/material";
import Paper from "@mui/material/Paper";
import Button from '@mui/material/Button';
import CardContent from '@mui/material/CardContent';
import FavoriteOutlinedIcon from '@mui/icons-material/FavoriteOutlined';
import { Icon } from '@iconify/react';
import Constant from "../../util/constants";
import { useGetTrip } from "../../react-query/hooks/useTrip";
import { Context } from "../../context";
import { useNavigate, useLocation, Link as RouterLink } from "react-router-dom";
import { useDeleteTrip } from "../../react-query/hooks/useTrip";
import { toggleFavourite } from "../../util/Display";
import "./Filter.css";

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === "dark" ? "#1A2027" : "#fff",
  ...theme.typography.body2,
  padding: theme.spacing(2),
  textAlign: "center",
  color: theme.palette.text.secondary,
}));

const styles = {
  accordianSummary: {
    paddingLeft: 0,
    paddingRight: 0
  },
  cardContent: {
    paddingBottom: 0
  },
  listItem: {
    paddingLeft: 0
  }
}

export default function Filter({ onDirectionHandler, onRequestClose, onFavouriteClick, isMapPage, ...props }) {
  const { auth, user } = useContext(Context);
  const { userDetail:{ favoriteLocations, _id } } = user;
  const isAuth = auth?.authenticated?.isAuth;
  const { data: trips, refetch } = useGetTrip(_id);
  const mutation = useDeleteTrip();
  const navigate = useNavigate();
  const currentLocation = useLocation();
  const currentPath = currentLocation.pathname;

  // Route-aware button visibility
  const isOnRequestCharger = currentPath === '/request-charger';
  const isOnAccount = currentPath === '/account';
  const isOnFeedback = currentPath === '/feedback';

  const handleRedirection = () => {
    navigate(Constant.paths.REQUEST_CHARGER);
  }

  const removeFavourite = (key) => () => {
    toggleFavourite(
      auth,
      user,
      { favourite: !0 },
      key
    )
  }

  const onClickFavourite = (loc) => () => {
    onFavouriteClick && onFavouriteClick(loc);
    onRequestClose();
  }

  const onClickSavedTrip = (e) => {
    const { google: { maps }, history, location } = window;
    const { tripStart, tripEnd, tripWaypoint, tripId } = e.currentTarget.dataset;
    const _tripStart = JSON.parse(tripStart);
    const _tripEnd = JSON.parse(tripEnd);
    const _tripWaypoint = JSON.parse(tripWaypoint);
    onDirectionHandler(
      { lat: _tripStart.latitude, lng: _tripStart.longitude },
      { lat: _tripEnd.latitude, lng: _tripEnd.longitude },
      _tripWaypoint.map((way) => ({ location: new maps.LatLng(way.latitude, way.longitude), stopover: true }))
    );
    history.pushState({ tripId }, '', `${location.href}?from=${tripStart}&to=${tripEnd}&tripId=${tripId}`);
    onRequestClose();
  }

  const onDeleteTrip = (key) => async (e) => {
    e.stopPropagation();
    const deletedTrip = await mutation.mutateAsync({ tripId: key });
    if(deletedTrip) {
      refetch();
    }
  }

  if (!isMapPage) {
    return (
      <div style={{ width: 360 }}>
        <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2, pb: 2 }}>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<Icon icon="eva:home-outline" width={20} height={20} />}
            component={RouterLink}
            to="/"
          >
            Home
          </Button>
          
          {!isOnRequestCharger && (
            <Button
              fullWidth
              variant="contained"
              onClick={handleRedirection}
              sx={{
                background: (theme) => `linear-gradient(135deg, var(--app-color) 0%, ${theme.palette.primary.dark} 100%)`,
                boxShadow: (theme) => `0 8px 16px 0 rgba(145, 158, 171, 0.24)`,
              }}
            >
              {Constant.label.REQUEST_FOR_CHARGER}
            </Button>
          )}
          
          {!isOnFeedback && (
            <Button
              fullWidth
              variant="outlined"
              startIcon={<Icon icon="eva:message-square-outline" width={20} height={20} />}
              component={RouterLink}
              to="/feedback"
            >
              {Constant.label.FEEDBACK_POPOVER}
            </Button>
          )}
          
          {!isOnAccount && isAuth && (
            <Button
              fullWidth
              variant="outlined"
              startIcon={<Icon icon="eva:person-outline" width={20} height={20} />}
              component={RouterLink}
              to="/account"
            >
              My Account
            </Button>
          )}
          
          {isAuth && (
            <Button
              fullWidth
              variant="outlined"
              color="error"
              startIcon={<Icon icon="eva:log-out-outline" width={20} height={20} />}
              component={RouterLink}
              to="/logout"
            >
              Log Out
            </Button>
          )}
        </CardContent>
      </div>
    );
  }

  return (
    <div style={{ width: 360 }}>
      {/* Map page specific Buttons on top */}
      <CardContent sx={{ pb: 1 }}>
        <Button
          fullWidth
          variant="contained"
          onClick={handleRedirection}
          sx={{
            background: (theme) => `linear-gradient(135deg, var(--app-color) 0%, ${theme.palette.primary.dark} 100%)`,
            boxShadow: (theme) => `0 8px 16px 0 rgba(145, 158, 171, 0.24)`,
          }}
        >
          {Constant.label.REQUEST_FOR_CHARGER}
        </Button>
      </CardContent>
      
      {/* Map page Accordions */}
      <CardContent sx={{ ...styles.cardContent, pt: 0 }}>
        <>
          <Accordion elevation={0}>
              <AccordionSummary sx={styles.accordianSummary}  expandIcon={<Icon icon="eva:arrow-ios-downward-fill" />} aria-controls='panel-1-content' id="panel-1-header">
                  <Typography variant="subtitle1" component="div" gutterBottom>{Constant.label.FAVOURITE_LIST}</Typography>
              </AccordionSummary>
              <AccordionDetails className="saved-trip-panel" sx={{ padding: 0 }}>
                  {favoriteLocations?.length > 0 && 
                  <List dense={!1}>
                    {favoriteLocations?.map(loc => (
                      <ListItem sx={{...styles.listItem, cursor: 'pointer', transition: 'none', '&:hover': { backgroundColor: 'rgba(145, 158, 171, 0.16)', borderRadius: '8px' }}} key={loc._id} onClick={onClickFavourite(loc)}>
                        <ListItemIcon>
                          <Tooltip title="Remove Favorite" placement="top">
                            <IconButton aria-label="favourite" onClick={(e) => { e.stopPropagation(); removeFavourite(loc._id)(); }} >
                            <FavoriteOutlinedIcon color="error" />
                            </IconButton>
                          </Tooltip>
                        </ListItemIcon>
                      <ListItemText 
                      primary={loc.name}
                      primaryTypographyProps={{ variant: "body2", sx: { fontSize: "0.85rem" } }}
                      />
                  </ListItem>
                    ))}
                  </List>}
  
                  {!favoriteLocations?.length && 
                  <Typography textAlign="center" variant="body2">{Constant.message.NO_FAVOURITE_LIST}</Typography>}
              </AccordionDetails>
          </Accordion>
          <Accordion elevation={0}>
          <AccordionSummary sx={styles.accordianSummary}  expandIcon={<Icon icon="eva:arrow-ios-downward-fill" />} aria-controls='panel-2-content' id="panel-2-header">
              <Typography variant="subtitle1" component="div" gutterBottom>{Constant.label.SAVED_TRIP}</Typography>
              </AccordionSummary>
              <AccordionDetails className="saved-trip-panel" sx={{ padding: 0 }}>
                  {trips?.data?.length > 0 && 
                  <List dense={!1}>
                    {trips.data?.map(trip => (
                      <ListItem 
                      onClick={onClickSavedTrip}
                      data-trip-id={trip?._id}
                      data-trip-start={JSON.stringify(trip?.from)} 
                      data-trip-end={JSON.stringify(trip?.to)} 
                      data-trip-waypoint={JSON.stringify(trip?.via)} 
                      sx={{...styles.listItem, cursor: 'pointer', transition: 'none', '&:hover': { backgroundColor: 'rgba(145, 158, 171, 0.16)', borderRadius: '8px' }}} 
                      key={trip._id}
                      >
                        <ListItemIcon>
                          <Tooltip title="Remove Trip" placement="top">
                            <IconButton onClick={onDeleteTrip(trip?._id)} aria-label="remove trip">
                              <Icon icon="eva:trash-2-outline" width={24} height={24} color="#FF4842" />
                            </IconButton>
                          </Tooltip>
                        </ListItemIcon>
                      <ListItemText 
                      primary={trip?.from?.address}
                      primaryTypographyProps={{ variant: "body2", sx: { fontSize: "0.85rem" } }}
                      />
                  </ListItem>
                    ))}
                  </List>}
  
                  {!trips?.data.length && 
                  <Typography textAlign="center" variant="body2">{Constant.message.NO_SAVED_TRIP}</Typography>}
              </AccordionDetails>
          </Accordion>
        </>
      </CardContent>

      <CardContent sx={{ pt: 1 }}>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<Icon icon="eva:message-square-outline" width={20} height={20} />}
          component={RouterLink}
          to="/feedback"
        >
          {Constant.label.FEEDBACK_POPOVER}
        </Button>
        {isAuth && (
          <Button
            fullWidth
            variant="outlined"
            startIcon={<Icon icon="eva:person-outline" width={20} height={20} />}
            component={RouterLink}
            to="/account"
            sx={{ mt: 2 }}
          >
            My Account
          </Button>
        )}
        {isAuth && (
          <Button
            fullWidth
            variant="outlined"
            color="error"
            startIcon={<Icon icon="eva:log-out-outline" width={20} height={20} />}
            component={RouterLink}
            to="/logout"
            sx={{ mt: 2 }}
          >
            Log Out
          </Button>
        )}
      </CardContent>
    </div>
  );
}
